import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Template } from '@/types';
import api from '@/lib/api';
import { inlineError, type InlineError } from '@/lib/errorReport';
import { isTemplateEmpty } from '@/lib/templateContent';

interface PageLibraryStore {
  /** Ook de gearchiveerde: het overzicht toont ze op verzoek, de editormenu's laten ze weg. */
  pages: Template[];
  loading: boolean;
  loaded: boolean;
  /** Laden mislukt; loaded blijft dan false, zodat het volgende menu het opnieuw probeert. */
  error: InlineError | null;
  fetchPages: () => Promise<void>;
  /** Haalt de lijst één keer op, ook als meerdere menu's tegelijk openen. */
  ensurePages: () => void;
}

/**
 * Bibliotheekpagina's zijn templates van het soort `page`. De backend beperkt de lijst
 * tot de portal van de sessie, dus er hoeft geen portal-ID mee.
 */
export const usePageLibraryStore = create<PageLibraryStore>()(
  devtools(
    (set, get) => ({
      pages: [],
      loading: false,
      loaded: false,
      error: null,

      fetchPages: async () => {
        set({ loading: true, error: null });
        try {
          const pages = await api.getTemplates({ kind: 'page', includePublic: false, includeArchived: true });
          set({ pages, loading: false, loaded: true });
        } catch (error) {
          set({ error: inlineError('Bibliotheek laden mislukt', error), loading: false });
        }
      },

      ensurePages: () => {
        const { loaded, loading, fetchPages } = get();
        if (!loaded && !loading) void fetchPages();
      },
    }),
    { name: 'PageLibraryStore' }
  )
);

export const NO_CATEGORY = 'Zonder categorie';
/** De standaardpagina's (cover, team, investering…) die de backend in elke bibliotheek zet. */
export const STANDARD_CATEGORY = 'Standaard';

/** Standaard vooraan, zonder categorie achteraan, de rest op naam. */
function compareCategories(a: string, b: string): number {
  const rank = (category: string) => (category === STANDARD_CATEGORY ? 0 : category === NO_CATEGORY ? 2 : 1);
  return rank(a) - rank(b) || a.localeCompare(b, 'nl');
}

/** Per categorie, in de volgorde van `pages` binnen elke groep. */
export function groupLibraryPages(pages: Template[]): [string, Template[]][] {
  const byCategory = new Map<string, Template[]>();
  for (const page of pages) {
    const category = page.category?.trim() || NO_CATEGORY;
    byCategory.set(category, [...(byCategory.get(category) ?? []), page]);
  }
  return [...byCategory.entries()].sort(([a], [b]) => compareCategories(a, b));
}

/** Standaardpagina's in documentvolgorde (cover eerst, sluiting achteraan), de rest op naam. */
export function compareInsertOrder(a: Template, b: Template): number {
  const order = (page: Template) =>
    typeof page.layoutConfig?.presetOrder === 'number' ? page.layoutConfig.presetOrder : Infinity;
  return order(a) - order(b) || a.name.localeCompare(b.name, 'nl');
}

/** Bibliotheekpagina's die je in een offerte kunt invoegen: niet gearchiveerd, per categorie. */
export function useInsertableLibraryPageGroups(): [string, Template[]][] {
  const pages = usePageLibraryStore((state) => state.pages);
  const ensurePages = usePageLibraryStore((state) => state.ensurePages);

  useEffect(() => {
    ensurePages();
  }, [ensurePages]);

  return useMemo(
    () =>
      groupLibraryPages(
        pages.filter((page) => !page.archivedAt && !isTemplateEmpty(page)).sort(compareInsertOrder)
      ),
    [pages]
  );
}
